// DEX by web services: the connection, sending a reporting window, and the request log.
// The API is routes/agedCareDexWs.js; what is sent is the same plan as the bulk file.
import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plug, Loader2, Save, RefreshCw, Send, Eye, RotateCcw, ScrollText, CheckCircle2, AlertTriangle, Settings2, ClipboardCheck, XCircle, MinusCircle } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, Field, DetailDrawer, DataTable, fmtDate, fmtDateTime } from './design';

const ENV_LABEL = { staging: 'Pre-production (testing)', production: 'Production' };
// What a record is, in words: the API's kind values are never shown as they come.
const KIND_LABEL = { client: 'Client', case: 'Case', session: 'Session' };
const OUTCOME = {
  success: ['Accepted', 'text-emerald-700'], warning: ['Accepted with a warning', 'text-amber-700'], not_found: ['Not in DEX yet', 'text-slate-500'],
  failed: ['Refused', 'text-red-800'], fault: ['Refused', 'text-red-800'], error: ['No answer', 'text-red-800'],
};
const OP_LABEL = {
  Ping: 'Test connection', GetReferenceData: 'Reference data', GetOrganisationActivities: 'Program activities', GetOutletActivities: 'Outlet activities',
  GetClient: 'Look up client', AddClient: 'Add client', UpdateClient: 'Update client', GetCase: 'Look up case', AddCase: 'Add case', UpdateCase: 'Update case',
  GetSession: 'Look up session', AddSession: 'Add session', UpdateSession: 'Update session', DeleteSession: 'Delete session',
  DeleteCase: 'Delete case', DeleteClient: 'Delete client',
};

// ── Going live: the pre-production test plan, then sign-off; production sending only after it ─
function GoLive({ s, onChanged }) {
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const g = s?.goLive || {};
  const steps = g.checks?.steps || [];
  const run = async () => {
    setBusy('run');
    try {
      const r = await api.post('/aged-care/dex/ws/test-plan');
      const bad = (r.data.steps || []).filter((x) => x.ok !== true).length;
      if (r.data.stopped) toast.error(r.data.stopped); else if (bad) toast.error(`${bad} step${bad === 1 ? '' : 's'} did not pass`); else toast.success('Every step passed');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not run the test plan'); } finally { setBusy(''); }
  };
  const signOff = async () => {
    setBusy('sign');
    try { await api.post('/aged-care/dex/ws/test-plan/signoff', { note }); toast.success('Signed off: production can be switched on'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not sign it off'); } finally { setBusy(''); }
  };
  const icon = (ok) => (ok === true ? <CheckCircle2 size={13} className="text-emerald-600" /> : ok === false ? <XCircle size={13} className="text-red-700" /> : <MinusCircle size={13} className="text-slate-400" />);
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2 space-y-2 text-[13px]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-slate-800 flex items-center gap-1.5"><ClipboardCheck size={14} /> Going live</span>
        <button type="button" className="btn-secondary btn-sm" onClick={run} disabled={!!busy || !s?.staging?.hasPassword}>
          {busy === 'run' ? <Loader2 size={13} className="animate-spin" /> : <ClipboardCheck size={13} />} Run the pre-production test plan
        </button>
      </div>
      <p className="text-[12px] text-slate-500">
        Before production, run the test plan on pre-production: it adds, updates and deletes a test client, case and session (with the Stage 3 fields),
        and checks DEX refuses a repeated request. Pre-production is not always available; ask the DEX helpdesk before you rely on it.
      </p>
      {g.signedAt
        ? <p className="text-emerald-700"><CheckCircle2 size={13} className="inline -mt-0.5" /> Signed off {fmtDateTime(g.signedAt)}{g.note ? `: ${g.note}` : ''}. Production can be used.</p>
        : <p className="text-amber-800">Production sending is off until the test plan is signed off.</p>}
      {steps.length > 0 && (
        <>
          <p className="text-[12px] text-slate-500">Last run {fmtDateTime(g.runAt)}{g.checks?.stopped ? `: ${g.checks.stopped}` : ''}</p>
          <ul className="space-y-0.5">
            {steps.map((x) => <li key={x.key} className="flex items-start gap-1.5 text-[12.5px]"><span className="mt-0.5">{icon(x.ok)}</span><span>{x.label}{x.message ? <span className="text-slate-500"> · {x.message}</span> : null}</span></li>)}
          </ul>
          {!g.signedAt && (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Note (needed if a core step did not pass)">
                {(id) => <input id={id} className="input-field !w-96" value={note} onChange={(e) => setNote(e.target.value)} placeholder="How each failed step was confirmed" />}
              </Field>
              <button type="button" className="btn-primary btn-sm" onClick={signOff} disabled={!!busy}>Sign off</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── The connection ────────────────────────────────────────────────────────────────────────────
export function DexConnection({ onSetupDone, onChange }) {
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState('');
  // A failed load says so instead of reading as "Not set up", which sent people off to ask the DEX
  // helpdesk for a web service user they already had.
  const load = useCallback(async () => {
    setError(null);
    try { const r = await api.get('/aged-care/dex/ws/settings'); setS(r.data); onChange?.(r.data); }
    catch (e) { setError(e.response?.data?.message || 'Could not load the connection settings'); }
  }, [onChange]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('save');
    try {
      const r = await api.put('/aged-care/dex/ws/settings', { environment: form.environment, credentialsFor: form.credentialsFor, username: form.username, password: form.password || undefined });
      setS(r.data); onChange?.(r.data); setForm(null); toast.success('Saved. Test the connection next.');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); } finally { setBusy(''); }
  };
  const ping = async () => {
    setBusy('ping');
    try {
      const r = await api.post('/aged-care/dex/ws/ping');
      if (r.data.ok) toast.success(r.data.message); else toast.error(r.data.message || 'DEX did not accept the connection');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not test the connection'); } finally { setBusy(''); }
  };
  const setup = async () => {
    setBusy('setup');
    try {
      const r = await api.post('/aged-care/dex/ws/setup');
      toast.success(`From DEX: ${r.data.codeTypes} code lists, ${r.data.activities} program activities, ${r.data.outlets} outlet activities`);
      onSetupDone?.();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not get your set-up from DEX'); } finally { setBusy(''); }
  };

  const configured = !!(s && s.username && s.hasPassword);
  const p = s?.lastPing;
  return (
    <Panel title="Connection to DEX (web services)" icon={Plug} className="mb-5" bodyClass="px-4 py-3 space-y-3"
      actions={s && <button type="button" className="btn-secondary btn-sm" onClick={() => setForm({ environment: s?.environment || 'staging', credentialsFor: s?.environment || 'staging', username: (s?.[s?.environment || 'staging'] || {}).username || '', password: '' })}><Settings2 size={13} /> {configured ? 'Settings' : 'Set up'}</button>}>
      <p className="text-[13px] text-slate-600">
        Send clients, cases and sessions straight to the Data Exchange instead of uploading a file. Each record is checked against what DEX already holds,
        so sending a month again only sends what changed, and a visit cancelled after it was sent is taken out.
      </p>
      {!s ? (
        error
          ? <p role="alert" className="text-[13px] text-red-800">{error} <button type="button" className="underline ml-1" onClick={load}>Try again</button></p>
          : <p className="text-[13px] text-slate-500"><Loader2 size={13} className="inline animate-spin -mt-0.5" /> Checking the connection…</p>
      ) : !configured ? (
        <p className="text-[13px] text-amber-800">
          Not set up. Ask the DEX helpdesk (dssdataexchange.helpdesk@dss.gov.au) for a <b>web service user</b> for your organisation, then enter it under Set up.
          Test on pre-production first.
        </p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
          <div className="min-w-0 text-[13px]">
            <div className="font-medium text-slate-800">{ENV_LABEL[s.environment] || s.environment} · {s.username}</div>
            <div className={clsx('text-[12px]', !p ? 'text-slate-500' : p.ok ? 'text-emerald-700' : 'text-red-800')}>
              {!p ? 'Not tested yet' : `${p.ok ? '' : 'Last test failed: '}${p.message} (${fmtDateTime(p.at)})`}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-secondary btn-sm" onClick={ping} disabled={!!busy}>{busy === 'ping' ? <Loader2 size={13} className="animate-spin" /> : <Plug size={13} />} Test connection</button>
            <button type="button" className="btn-secondary btn-sm" onClick={setup} disabled={!!busy}>{busy === 'setup' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Get set-up from DEX</button>
          </div>
        </div>
      )}
      {s && error && <p role="alert" className="text-[12.5px] text-red-800">{error} <button type="button" className="underline ml-1" onClick={load}>Try again</button></p>}
      {s?.signinRefusedAt && (
        <p role="alert" className="text-[12.5px] font-medium text-red-800">
          <AlertTriangle size={12} className="inline -mt-0.5" /> DEX refused the sign-in on {fmtDateTime(s.signinRefusedAt)}. Nothing is sent until the user name and password are entered again under Settings
          (5 refusals lock the account; the DEX helpdesk unlocks it).
        </p>
      )}
      {s && (s.staging?.hasPassword || s.production?.hasPassword) && (
        <p className="text-[12px] text-slate-500">
          Pre-production user: {s.staging?.hasPassword ? s.staging.username : 'not set'} · Production user: {s.production?.hasPassword ? s.production.username : 'not set'}
        </p>
      )}
      {s && s.staging?.hasPassword && <GoLive s={s} onChanged={load} />}

      {form && (
        <DetailDrawer open onClose={() => setForm(null)} icon={Plug} width="sm" title="DEX web services" subtitle="Your organisation's web service user"
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={() => setForm(null)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={save} disabled={busy === 'save'}><Save size={15} /> {busy === 'save' ? 'Saving…' : 'Save'}</button>
            </>
          )}>
          <div className="space-y-4">
            <Field label="In use" hint="Pre-production is DSS's test system: nothing sent there is reported. Production can be chosen once the test plan is signed off.">
              {(id) => (
                <select id={id} className="input-field" value={form.environment} onChange={(e) => setForm((f) => ({ ...f, environment: e.target.value }))}>
                  <option value="staging">{ENV_LABEL.staging}</option>
                  <option value="production" disabled={!s?.goLive?.signedAt}>{ENV_LABEL.production}{s?.goLive?.signedAt ? '' : ' (after sign-off)'}</option>
                </select>
              )}
            </Field>
            <Field label="Web service user for" hint="Each system has its own web service user from the DEX helpdesk.">
              {(id) => (
                <select id={id} className="input-field" value={form.credentialsFor} onChange={(e) => setForm((f) => ({ ...f, credentialsFor: e.target.value, username: (s?.[e.target.value] || {}).username || '', password: '' }))}>
                  <option value="staging">{ENV_LABEL.staging}</option>
                  <option value="production">{ENV_LABEL.production}</option>
                </select>
              )}
            </Field>
            <Field label="Web service user name">
              {(id) => <input id={id} className="input-field" autoComplete="off" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} />}
            </Field>
            <Field label="Password" hint={s?.[form.credentialsFor]?.hasPassword ? 'Saved. Leave blank to keep it.' : 'DSS web service passwords are 10 to 15 characters with upper and lower case, a number and a symbol. Five wrong attempts lock the account.'}>
              {(id) => <input id={id} type="password" className="input-field" autoComplete="new-password" value={form.password} placeholder={s?.[form.credentialsFor]?.hasPassword ? '••••••••••' : ''} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />}
            </Field>
            <p className="text-[12px] text-slate-500">The password is stored encrypted, is only ever sent to DSS, and never appears in the request log.</p>
          </div>
        </DetailDrawer>
      )}
    </Panel>
  );
}

// ── Preview and send a window ─────────────────────────────────────────────────────────────────
const Counts = ({ r }) => (
  <table className="w-full text-[12.5px] tabular-nums">
    <thead><tr className="text-slate-500 text-left"><th className="py-1 font-medium" /><th className="font-medium">New</th><th className="font-medium">Changed</th><th className="font-medium">Unchanged</th><th className="font-medium">Removed</th><th className="font-medium">Refused</th><th className="font-medium">Held back</th></tr></thead>
    <tbody>
      {[['Clients', r.clients], ['Cases', r.cases], ['Sessions', r.sessions]].map(([label, t]) => (
        <tr key={label} className="border-t border-slate-100">
          <td className="py-1 text-slate-700">{label}</td><td>{t.add}</td><td>{t.update}</td><td className="text-slate-500">{t.unchanged}</td>
          <td>{t.delete || 0}</td><td className={t.failed ? 'text-red-800 font-medium' : ''}>{t.failed}</td><td className={t.skipped ? 'text-amber-700' : ''}>{t.skipped}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export function DexSend({ range, connected, environment, onSent }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const run = async (kind) => {
    // Sending goes to the Department straight away and cannot be called back from here (a session
    // sent by mistake has to be deleted in DEX), so it asks first. A preview sends nothing.
    if (kind === 'send') {
      const env = environment || preview?.environment;
      const where = env === 'production' ? 'the Department of Social Services, as your report'
        : env === 'staging' ? 'DEX pre-production, the Department\'s test system (nothing sent there is reported)'
          : 'the Department\'s Data Exchange';
      const ok = await confirm(
        `The clients, cases and sessions from ${fmtDate(range[0])} to ${fmtDate(range[1])} are sent to ${where}. Records DEX already holds are checked first, so only what changed is sent.`,
        { title: 'Send these records to DEX?', confirmLabel: 'Send to DEX' },
      );
      if (!ok) return;
    }
    setBusy(kind);
    try {
      const r = await api.post(`/aged-care/dex/ws/${kind}`, { from: range[0], to: range[1] });
      if (kind === 'preview') { setPreview(r.data); setResult(null); } else {
        setResult(r.data); setPreview(null); onSent?.();
        const f = (r.data.failures || []).length;
        if (r.data.stopped) toast.error(r.data.stopped.message);
        else if (f) toast.error(`${f} record${f === 1 ? '' : 's'} refused by DEX. See below.`); else toast.success('Sent to DEX');
      }
    } catch (e) { toast.error(e.response?.data?.message || (kind === 'send' ? 'Could not send to DEX' : 'Could not preview')); } finally { setBusy(''); }
  };
  const shown = result || preview;
  return (
    <div className="mt-4 rounded-lg border border-slate-200 p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] font-medium text-slate-800">Or send it by web services</span>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary btn-sm" onClick={() => run('preview')} disabled={!!busy || !connected}>{busy === 'preview' ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Preview</button>
          <button type="button" className="btn-primary btn-sm" onClick={() => run('send')} disabled={!!busy || !connected}>{busy === 'send' ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send to DEX</button>
        </div>
      </div>
      {!connected && <p className="text-[12px] text-slate-500">Set up the connection to DEX above to send without a file.</p>}
      {shown && (
        <div className="space-y-2">
          <p className="text-[12.5px] text-slate-600">
            {result ? 'Sent' : 'Would send'} {fmtDate(shown.from)} to {fmtDate(shown.to)} to {ENV_LABEL[shown.environment] || shown.environment}:
          </p>
          <Counts r={shown} />
          {shown.stopped && <p role="alert" className="text-[12.5px] font-medium text-red-800"><AlertTriangle size={12} className="inline -mt-0.5" /> {shown.stopped.message}</p>}
          {(shown.failures || []).length > 0 && (
            <ul className="text-[12.5px] text-red-800 space-y-0.5">
              {shown.failures.slice(0, 20).map((f) => <li key={`${f.kind}-${f.key}`}><AlertTriangle size={12} className="inline -mt-0.5" /> {KIND_LABEL[f.kind] || 'Record'} {f.name ? `(${f.name})` : ''}{f.date ? ` ${fmtDate(f.date)}` : ''}: {f.error}</li>)}
            </ul>
          )}
          {(shown.excluded || []).length > 0 && (
            <p className="text-[12.5px] text-red-800">Left out until fixed: {shown.excluded.map((x) => `${x.name} (${x.reasons.join('; ')})`).join(' · ')}</p>
          )}
          {shown.notes?.unmappedServices?.length > 0 && <p className="text-[12.5px] text-amber-800">Not included, service not mapped: {shown.notes.unmappedServices.map((u) => `${u.name} (${u.sessions})`).join(', ')}</p>}
          {shown.notes?.blockedSessions?.length > 0 && <p className="text-[12.5px] text-amber-800">Not included: {shown.notes.blockedSessions.map((u) => `${u.name} (${u.sessions})`).join('; ')}</p>}
          {result && !result.stopped && !(result.failures || []).length && <p className="text-[12.5px] text-emerald-700"><CheckCircle2 size={13} className="inline -mt-0.5" /> DEX accepted everything sent.</p>}
        </div>
      )}
    </div>
  );
}

// ── The request log ───────────────────────────────────────────────────────────────────────────
export function DexRequestLog({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [problems, setProblems] = useState(false);
  const [warningsOnly, setWarningsOnly] = useState(false);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  // A failed load is a failure with a retry, not "Nothing sent yet".
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = warningsOnly ? await api.get('/aged-care/dex/ws/warnings') : await api.get('/aged-care/dex/ws/log', { params: problems ? { problems: 'true' } : {} });
      setRows(Array.isArray(r.data) ? r.data.map((x) => ({ ...x, outcome: x.outcome || 'warning' })) : []);
    } catch (e) { setRows([]); setError(e.response?.data?.message || 'Could not load the request log'); } finally { setLoading(false); }
  }, [problems, warningsOnly]);
  const reviewed = async (r) => {
    try { await api.post(`/aged-care/dex/ws/log/${r.id}/reviewed`); toast.success('Reviewed'); load(); } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  useEffect(() => { load(); }, [load, refreshKey]);
  const view = async (r) => { try { const x = await api.get(`/aged-care/dex/ws/log/${r.id}`); setOpen(x.data); } catch { toast.error('Could not open it'); } };
  const retry = async (r) => {
    setBusy(r.id);
    try {
      const x = await api.post(`/aged-care/dex/ws/log/${r.id}/retry`);
      const f = (x.data.failures || []).length;
      if (f) toast.error(`DEX still refused it: ${x.data.failures[0].error}`); else toast.success('Sent again and accepted');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not run it again'); } finally { setBusy(null); }
  };
  const columns = [
    { key: 'created_at', header: 'When', width: 150, render: (r) => <span className="tabular-nums text-slate-600">{fmtDateTime(r.created_at)}</span> },
    { key: 'operation', header: 'Request', width: 160, render: (r) => <span className="text-slate-800">{OP_LABEL[r.operation] || 'Request to DEX'}</span> },
    { key: 'name', header: 'Record', render: (r) => <span className="text-slate-700">{r.name || '—'}<span className="block text-[11px] font-mono text-slate-400">{r.dex_key || ''}</span></span> },
    {
      key: 'outcome', header: 'DEX said', width: 260, render: (r) => {
        const [w, cls] = OUTCOME[r.outcome] || [r.outcome, 'text-slate-600'];
        return <span className={clsx('text-[12.5px]', cls)}>{w}{r.message && r.outcome !== 'success' && r.outcome !== 'not_found' ? <span className="block text-slate-600">{r.message}</span> : null}</span>;
      },
    },
    { key: 'environment', header: 'To', width: 110, render: (r) => <span className="text-[12px] text-slate-500">{r.environment === 'production' ? 'Production' : 'Pre-production'}</span> },
  ];
  return (
    <Panel title="Request log" icon={ScrollText}
      actions={(
        <span className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-[12.5px] text-slate-600">
            <input type="checkbox" checked={warningsOnly} onChange={(e) => setWarningsOnly(e.target.checked)} /> Warnings to review
          </label>
          <label className="flex items-center gap-2 text-[12.5px] text-slate-600">
            <input type="checkbox" checked={problems} onChange={(e) => setProblems(e.target.checked)} disabled={warningsOnly} /> Problems only
          </label>
        </span>
      )}>
      <DataTable columns={columns} rows={rows} rowKey="id" loading={loading} error={error} onRetry={load} stickyHeader={false} label="DEX requests"
        onRowClick={view}
        rowActions={(r) => (r.outcome === 'warning' && warningsOnly ? (
          <button type="button" className="btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); reviewed(r); }}><CheckCircle2 size={13} /> Reviewed</button>
        ) : ['failed', 'fault', 'error'].includes(r.outcome) && ['client', 'case', 'session'].includes(r.kind) ? (
          <button type="button" className="btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); retry(r); }} disabled={busy === r.id}>
            {busy === r.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Run again
          </button>
        ) : null)}
        empty={{ icon: ScrollText, title: 'Nothing sent yet', hint: 'Every request to DEX, and what DEX answered, is kept here.' }}
        footer={rows.length ? 'Select a request to see exactly what was sent and what DEX answered. Passwords are never kept.' : null}
      />
      {open && (
        <DetailDrawer open onClose={() => setOpen(null)} icon={ScrollText} width="lg" title={OP_LABEL[open.operation] || 'Request to DEX'}
          subtitle={`${fmtDateTime(open.created_at)} · request ${open.client_request_id || '—'}${open.http_status ? ` · HTTP ${open.http_status}` : ''}`}>
          <div className="space-y-3">
            {open.message && <p className="text-[13px] text-slate-700">{open.message}</p>}
            {Array.isArray(open.messages) && open.messages.length > 0 && (
              <ul className="text-[12.5px] text-slate-700 space-y-1">{open.messages.map((m, i) => <li key={i}><b>{m.level}</b> {m.id ? `#${m.id} ` : ''}{m.description}{m.field ? ` (${m.field})` : ''}</li>)}</ul>
            )}
            <div>
              <div className="text-[12px] font-medium text-slate-600 mb-1">Sent</div>
              <pre className="max-h-72 overflow-auto rounded-lg bg-slate-50 p-2 text-[11px] leading-snug whitespace-pre-wrap break-all">{open.request_xml}</pre>
            </div>
            <div>
              <div className="text-[12px] font-medium text-slate-600 mb-1">DEX answered</div>
              <pre className="max-h-72 overflow-auto rounded-lg bg-slate-50 p-2 text-[11px] leading-snug whitespace-pre-wrap break-all">{open.response_xml || '(no answer)'}</pre>
            </div>
          </div>
        </DetailDrawer>
      )}
    </Panel>
  );
}

export default DexConnection;
